import { type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Icon } from './icon';
import {
  errorCircleIcon,
  warningTriangleIcon,
  successCircleIcon,
  infoCircleIcon,
  dismissIcon,
} from '../../icons';

const alertVariants = cva('relative w-full rounded-lg border p-4 text-sm', {
  variants: {
    variant: {
      default: 'bg-background text-foreground border-border',
      destructive: 'border-red-200 bg-red-50 text-red-700',
      warning: 'border-yellow-200 bg-yellow-50 text-yellow-700',
      success: 'border-green-200 bg-green-50 text-green-700',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const iconColors: Record<string, string> = {
  default: 'text-foreground',
  destructive: 'text-red-400',
  warning: 'text-yellow-400',
  success: 'text-green-400',
};

const dismissColors: Record<string, string> = {
  default: 'text-muted-foreground hover:text-foreground',
  destructive: 'text-red-400 hover:text-red-500',
  warning: 'text-yellow-400 hover:text-yellow-500',
  success: 'text-green-400 hover:text-green-500',
};

const variantIcons: Record<string, string> = {
  default: infoCircleIcon,
  destructive: errorCircleIcon,
  warning: warningTriangleIcon,
  success: successCircleIcon,
};

export interface AlertProps extends VariantProps<typeof alertVariants> {
  title?: string;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
  id?: string;
}

function Alert({ variant = 'default', title, children, onDismiss, className, id }: AlertProps) {
  const v = variant ?? 'default';
  const iconSrc = variantIcons[v];
  const iconColor = iconColors[v];
  const dismissColor = dismissColors[v];

  return (
    <div id={id} role="alert" className={cn(alertVariants({ variant }), className)}>
      <div className="flex items-start">
        <div className="shrink-0">
          <Icon src={iconSrc} className={cn('h-5 w-5', iconColor)} />
        </div>
        <div className="ml-3 flex-1">
          {title && <h5 className="mb-1 font-medium leading-none tracking-tight">{title}</h5>}
          <div className="text-sm [&_p]:leading-relaxed">{children}</div>
        </div>
        {onDismiss && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDismiss}
            className={cn('ml-3 h-auto w-auto shrink-0 p-0 hover:bg-transparent', dismissColor)}
            aria-label="Dismiss"
          >
            <Icon src={dismissIcon} className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export { Alert, alertVariants };
